import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck, RefreshCw, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationRow } from './notifications/NotificationRow';
import { DeleteNotificationsDialog } from './notifications/DeleteNotificationsDialog';
import { notificationLink } from './notifications/notificationContent';

const NotificationBell = () => {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const inbox = useNotifications(user?.id);
  const { items, unread, loading, busy, error, refresh, markRead, remove, removeAll } = inbox;
  if (!user) return null;
  const openNotification = async (item) => {
    if (!item.read && !await markRead(item.id)) return;
    setOpen(false);
    navigate(notificationLink(item));
  };
  return <>
    <Popover open={open} onOpenChange={(value) => { if (!confirmDelete) { setOpen(value); if (value) refresh(); } }}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={t('notification_center.title')}
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground transition-colors hover:bg-secondary" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full gold-gradient px-1 text-[10px] font-bold leading-none text-black shadow"
            data-testid="notifications-bell-badge">{unread > 99 ? '99+' : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={8} className="w-96 max-w-[calc(100vw-16px)] overflow-hidden border-border bg-card p-0" data-testid="notifications-panel">
        <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <Bell className="h-4 w-4 shrink-0 gold-text" />
            <div><h2 className="text-sm font-semibold" data-testid="notifications-title">{t('notification_center.title')}</h2>
              <p className="text-[11px] text-muted-foreground" data-testid="notifications-unread-count">{t('notification_center.unread_count', { count: unread })}</p></div>
          </div>
          <Button type="button" size="icon" variant="ghost" disabled={loading || !!busy} onClick={refresh}
            aria-label={t('notification_center.refresh')} title={t('notification_center.refresh')} data-testid="notifications-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </header>
        <div className="flex flex-wrap gap-1 border-b border-border px-2 py-2">
          <Button type="button" variant="ghost" size="sm" disabled={!unread || !!busy} onClick={() => markRead()}
            className="h-8 gap-1.5 px-2 text-xs" data-testid="notifications-mark-all-read"><CheckCheck className="h-4 w-4 shrink-0" />{t('notification_center.mark_all')}</Button>
          <Button type="button" variant="ghost" size="sm" disabled={!items.length || !!busy} onClick={() => setConfirmDelete(true)}
            className="h-8 gap-1.5 px-2 text-xs text-red-500" data-testid="notifications-delete-all"><Trash2 className="h-4 w-4 shrink-0" />{t('notification_center.delete_all')}</Button>
        </div>
        {error && <p role="alert" className="px-3 py-2 text-xs text-red-500" data-testid="notifications-error">{t('notification_center.load_failed')}</p>}
        {!items.length ? <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
          <Bell className="mx-auto mb-2 h-6 w-6 opacity-40" />{t(loading ? 'notification_center.loading' : 'notification_center.empty')}
        </div> : <ul className="max-h-[min(60dvh,480px)] divide-y divide-border overflow-y-auto" data-testid="notifications-list">
          {items.map((item) => <NotificationRow key={item.id} item={item} busy={busy}
            onOpen={() => openNotification(item)} onRead={() => markRead(item.id)} onDelete={() => remove(item.id)} />)}
        </ul>}
      </PopoverContent>
    </Popover>
    <DeleteNotificationsDialog open={confirmDelete} onOpenChange={setConfirmDelete} busy={busy} onConfirm={removeAll} />
  </>;
};

export default NotificationBell;